/**
 * Toast deduplication registry.
 *
 * Sonner's `create()` updates a toast in place when it sees an id it already
 * holds, rather than appending a second one. That is what lets us collapse
 * repeats without touching any of the ~1,800 `toast.*` call sites: we derive a
 * stable id from the toast's own content and hand it back to sonner.
 *
 * Keying is exact-match on kind + title + description. Two different failures
 * ("Couldn't save task 123" / "…456") must stay two toasts — merging them would
 * destroy error signal, and errors are ~60% of all toasts in this app.
 */

export type DedupedKind = 'success' | 'error' | 'info' | 'warning' | 'message'

/** NUL can't appear in user-facing copy, so it can't forge a key collision. */
const SEP = '\u0000'

export interface ToastEntry {
  /** How many times this exact toast has fired while live. */
  count: number
  /** Stable sonner id. Same id on a repeat => update in place. */
  id: string
}

const registry = new Map<string, ToastEntry>()

/**
 * The Map key carries identity; the sonner id is just a handle that has to stay
 * stable for the entry's lifetime and unique among live toasts. A counter gives
 * both without encoding the key (which contains NUL) into a value sonner may
 * put somewhere DOM-adjacent.
 */
let sequence = 0

/**
 * Derive a dedup key, or null when the toast can't be safely deduped.
 *
 * A non-string title (a ReactNode) has no stable identity we can hash, so those
 * fall through to sonner unchanged rather than risk merging unrelated toasts.
 */
export function deriveToastKey(
  kind: DedupedKind,
  title: unknown,
  description?: unknown
): string | null {
  if (typeof title !== 'string' || title.length === 0) {
    return null
  }
  const desc = typeof description === 'string' ? description : ''
  return `${kind}${SEP}${title}${SEP}${desc}`
}

/** Register a fire against `key`, returning the entry with its updated count. */
export function registerToast(key: string): ToastEntry {
  const existing = registry.get(key)
  if (existing) {
    existing.count += 1
    return existing
  }
  sequence += 1
  const entry: ToastEntry = { id: `trellis-toast:${sequence}`, count: 1 }
  registry.set(key, entry)
  return entry
}

/** Drop a key once its toast is gone, so the next fire starts a fresh card. */
export function releaseToast(key: string): void {
  registry.delete(key)
}

export function peekToast(key: string): ToastEntry | undefined {
  return registry.get(key)
}

/** Test-only. */
export function resetToastRegistry(): void {
  registry.clear()
  sequence = 0
}
